import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { k, useCss } from 'kremling'
import { isEmpty, sortBy } from 'lodash'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { DatePicker } from '@components/date-picker.component'
import { EmptyState } from '@components/empty-state.component'
import { Loader } from '@components/loader.component'
import { Button } from '@components/mantine/button.component'
import { GroupedSelect } from '@components/mantine/grouped-select.component'
import { Select } from '@components/mantine/select.component'
import { TextInput } from '@components/mantine/text-input.component'
import { Modal } from '@components/modal.component'
import { Required } from '@components/required.component'
import { useLoad } from '@hooks/use-load.hook'
import { useProcessWorkflowEndDate } from '@hooks/use-process-workflow-end-date.hook'
import { useAppState } from '@src/app.state'
import { db, functions } from '@src/firebase-app'
import { dbNames } from '@utils/constants'
import { getTodayDate } from '@utils/helpers'
import { toastService } from '@utils/toast.service'

type Props = {
  onResolve: (data: { newActivityId: any }) => void
  onReject: () => void
  template?: Template | null
  orgId?: string
}

export function SelfAddWorkflowModal({ onResolve, onReject, template, orgId }: Props) {
  const scope = useCss(css)
  const user = useAppState(state => state.user)
  const orgs = useAppState(state => state.orgs)
  const [saving, setSaving] = useState(false)
  const [selectedStartDate, setSelectedStartDate] = useState<any>(null)
  const [selectedName, setSelectedName] = useState(template?.name || '')
  const [selectedTemplate, setSelectedTemplate] = useState<any>(template || null)
  const [processedEndDate, setSelectedEndDate, endDateIsGenerated, ruleMeta] =
    useProcessWorkflowEndDate(selectedTemplate, selectedTemplate?.sections || [], selectedStartDate) as any
  const [selectedOrg, setSelectedOrg] = useState<Organization | null | undefined>(
    orgId ? orgs.find(org => org.id === orgId) : null,
  )
  const isAdmin = ['owner', 'manager', 'editor'].includes(
    user.orgRoles[selectedOrg?.id],
  )

  const [
    workflowTemplates,
    _setWorkflowTemplates,
    { loading: workflowTemplatesLoading },
  ] = useLoad(
    [],
    async () => {
      if (!isAdmin || !selectedOrg) return Promise.resolve([])
      const q = query(
        collection(db, dbNames.workflowTemplates),
        where('orgId', '==', selectedOrg.id),
        where('assistant', '==', false),
      )
      const templates = await getDocs(q).then(q =>
        q.docs.map(d => ({ ...d.data(), id: d.id })),
      )
      return sortBy(templates, 'name')
    },
    [selectedOrg],
  )

  const [
    publishedTemplates,
    _setPublishedTemplates,
    { loading: publishedTemplatesLoading },
  ] = useLoad(
    [],
    () => {
      if (!selectedOrg) return Promise.resolve([])
      const q = query(
        collection(db, dbNames.publishedTemplates),
        orderBy('name'),
        where('orgId', '==', selectedOrg.id),
        where('publishedLocations', 'array-contains', 'workspace'),
      )
      return getDocs(q).then(q => q.docs.map(d => ({ ...d.data(), id: d.id })))
    },
    [selectedOrg],
  )

  // Workspace options for select
  const orgSelectData = useMemo(
    () => orgs.map(org => ({ value: org.id, label: org.name })),
    [orgs],
  )

  // All templates combined for lookup
  const allTemplates = useMemo(
    () => [...publishedTemplates, ...workflowTemplates],
    [publishedTemplates, workflowTemplates],
  )

  // Grouped template data for admin users
  const groupedTemplateData = useMemo(
    () => [
      {
        group: 'Published',
        icon: 'globe-stand',
        items: publishedTemplates.map(t => ({ value: t.id, label: t.name })),
      },
      {
        group: 'Unpublished',
        icon: 'ballot',
        items: workflowTemplates.map(t => ({ value: t.id, label: t.name })),
      },
    ],
    [publishedTemplates, workflowTemplates],
  )

  // Simple template data (workflowTemplates only)
  const simpleWorkflowTemplateData = useMemo(
    () => workflowTemplates.map(t => ({ value: t.id, label: t.name })),
    [workflowTemplates],
  )

  // Simple template data (publishedTemplates only)
  const simplePublishedTemplateData = useMemo(
    () => publishedTemplates.map(t => ({ value: t.id, label: t.name })),
    [publishedTemplates],
  )

  // useEffect(() => {
  //   if (!selectedTemplate) return
  //   const { section, meta, endDate } = buildRuleMeta(
  //     selectedTemplate.sections,
  //     selectedStartDate,
  //   )
  //   if (selectedStartDate && section) {
  //     if (meta === metas.RECURRENCE_AND_REPEAT) {
  //       setSelectedEndDate(endDate)
  //     }
  //   }
  // }, [selectedStartDate])

  async function build() {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const startDate = selectedStartDate.toISO({ includeOffset: false })
    const endDate = processedEndDate.toISO({ includeOffset: false })
    const data = {
      startDate: {
        date: startDate,
        timeZone,
      },
      endDate: {
        date: endDate,
        timeZone,
      },
      userTimeZone: timeZone,
      description: '',
      selectedTemplate,
      ruleMeta,
      selectedName,
      orgId,
      today: getTodayDate().toISO(),
    }
    setSaving(true)
    try {
      const userIds = [user.id]
      const res = await httpsCallable(
        functions,
        'activityFunctions-createActivity',
      )({
        ...data,
        userIds,
      })
      onResolve({ newActivityId: res.data })
    } catch (err) {
      console.log(err)
      toastService.error('There was a problem assigning the activity')
      setSaving(false)
    }
  }

  // async function build() {
  //   debugger
  //   const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  //   const startDate = selectedStartDate.toISO()
  //   const endDate = selectedEndDate.toISO()
  //   const createActivity = httpsCallable(
  //     functions,
  //     'activityFunctions-createActivity',
  //   )
  //   const data = {
  //     startDate: {
  //       date: startDate,
  //       timeZone,
  //     },
  //     endDate: {
  //       date: endDate,
  //       timeZone,
  //     },
  //     userTimeZone: timeZone,
  //     userIds: [user.id],
  //     selectedTemplate,
  //     ruleMeta,
  //     selectedName,
  //     orgId: selectedOrg.id,
  //     today: getTodayDate().toISO(),
  //   }
  //   setSaving(true)
  //   const newActivityId = await createActivity(data)
  //
  //   onResolve({ newActivityId })
  // }

  function handleTemplateChange(templateId: string) {
    const t = allTemplates.find(t => t.id === templateId)
    if (selectedTemplate) {
      setSelectedStartDate(null)
      setSelectedEndDate(null)
    }
    if (!selectedName || selectedName === selectedTemplate?.name) {
      setSelectedName(t?.name || '')
    }
    setSelectedTemplate(t)
  }

  function renderTemplateSelection() {
    if (isAdmin) {
      if (!isEmpty(publishedTemplates)) {
        return (
          <GroupedSelect
            data={groupedTemplateData}
            onChange={handleTemplateChange}
            value={selectedTemplate?.id}
            searchable
            fullWidth
            placeholder="Select template"
          />
        )
      }

      return (
        <Select
          data={simpleWorkflowTemplateData}
          onChange={handleTemplateChange}
          value={selectedTemplate?.id}
          searchable
          fullWidth
          placeholder="Select template"
        />
      )
    }

    return (
      <Select
        data={simplePublishedTemplateData}
        onChange={handleTemplateChange}
        value={selectedTemplate?.id}
        searchable
        fullWidth
        placeholder="Select template"
      />
    )
  }

  function renderInfoOverlay() {
    if (!!selectedOrg) {
      if (!orgId) {
        if (
          (!workflowTemplatesLoading || !publishedTemplatesLoading) &&
          isEmpty(workflowTemplates)
        ) {
          if (isAdmin) {
            return (
              <Modal.Overlay>
                <EmptyState
                  title="Before you can start a workflow, you need to create a template."
                  subtitle={
                    <span>
                      Create a template on the{' '}
                      <Link
                        onClick={onReject}
                        to={`/${selectedOrg.id}/library`}
                      >
                        Templates
                      </Link>{' '}
                      to get started!
                    </span>
                  }
                  action={() => setSelectedOrg(null)}
                  actionText="Change workspace"
                />
              </Modal.Overlay>
            )
          } else {
            return (
              <Modal.Overlay>
                <EmptyState
                  title="Your workspace doesn't have any templates yet."
                  subtitle="Contact your workspace's Owner to get more information"
                  action={() => setSelectedOrg(null)}
                  actionText="Change workspace"
                />
              </Modal.Overlay>
            )
          }
        }
      }
      if (isAdmin) {
        if (!workflowTemplatesLoading && isEmpty(workflowTemplates)) {
          return (
            <Modal.Overlay>
              <EmptyState
                title="Before you can start a workflow, you need to create a template."
                subtitle={
                  <span>
                    Create a template on the{' '}
                    <Link
                      onClick={onReject}
                      to="/templates"
                    >
                      Templates
                    </Link>{' '}
                    to get started!
                  </span>
                }
                action={() => onReject()}
                actionText="Close"
              />
            </Modal.Overlay>
          )
        }
      } else {
        if (!publishedTemplatesLoading && isEmpty(workflowTemplates)) {
          return (
            <Modal.Overlay>
              <EmptyState
                title="Your workspace doesn't have any templates yet."
                subtitle="Contact your workspace's Owner to get more information"
                action={() => onReject()}
                actionText="Close"
              />
            </Modal.Overlay>
          )
        }
      }

      return null
    }
  }

  function disableSubmit() {
    return (
      !selectedOrg ||
      !selectedTemplate ||
      !selectedStartDate ||
      !processedEndDate ||
      !selectedName
    )
  }

  return (
    <>
      <Modal.Header>Start activity</Modal.Header>
      <Modal.Body {...scope}>
        {!orgId && (
          <div className="row break-1">
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="workflow-select">
                  Workspace <Required />
                </label>
                <Select
                  data={orgSelectData}
                  onChange={(orgIdVal: string) => {
                    const org = orgs.find(o => o.id === orgIdVal)
                    setSelectedOrg(org)
                  }}
                  value={selectedOrg?.id}
                  fullWidth
                  placeholder="Select workspace"
                />
              </div>
            </div>
          </div>
        )}
        {!template && (
          <div className="row break-1">
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="workflow-select">
                  Template <Required />
                </label>
                {renderTemplateSelection()}
              </div>
            </div>
          </div>
        )}
        <div className="row break-1">
          <div className="col-6">
            <div className="form-group">
              <label>
                Start date <Required />
              </label>
              <DatePicker
                placeholder="Select date"
                fixedContent
                onChange={setSelectedStartDate}
                value={selectedStartDate}
                disabled={!selectedTemplate}
                triggerIsBlock
              />
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              {endDateIsGenerated ? (
                <label>Generated end date</label>
              ) : (
                <label>
                  End date <Required />
                </label>
              )}
              <DatePicker
                placeholder={
                  endDateIsGenerated ? 'Select a start date' : 'Select date'
                }
                onChange={setSelectedEndDate}
                value={processedEndDate}
                triggerIsBlock
                fixedContent
                disabled={
                  !selectedStartDate || endDateIsGenerated || !selectedTemplate
                }
                minDate={selectedStartDate?.plus({ days: 1 }).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                })}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>
            Name <Required />
          </label>
          <TextInput
            value={selectedName}
            onChange={setSelectedName}
          />
        </div>
        {endDateIsGenerated && (
          <div className="form-group">
            <strong>Note:</strong> This template contains section rules that
            automatically determine the end date based off of the start date.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer {...scope}>
        <Button
          variant="tertiary"
          onClick={onReject}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={build}
          loading={saving}
          disabled={disableSubmit()}
        >
          Start activity
        </Button>
      </Modal.Footer>
      {saving && (
        <Modal.Overlay>
          <Loader />
        </Modal.Overlay>
      )}
      {renderInfoOverlay()}
    </>
  )
}

const css = k`

`
